import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import './App.css';

// Log build version on initialization
console.log("VALORAMAP build:", process.env.REACT_APP_GIT_SHA || "dev");

// Layout Components
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';

// Pages
import LandingPage from './pages/LandingPage';
import ScreenerPage from './pages/ScreenerPage';
import StockDetailPage from './pages/StockDetailPage';
import WatchlistPage from './pages/WatchlistPage';
import PricingPage from './pages/PricingPage';
import LoginPage from './pages/LoginPage';
import MarketsPage from './pages/MarketsPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import CGVPage from './pages/CGVPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ValuationGuidePage from './pages/ValuationGuidePage';
import AuthCallbackPage from './pages/AuthCallbackPage';
import ReportsPage from './pages/ReportsPage';
import ReportDetailPage from './pages/ReportDetailPage';
import OpportunitiesDashboard from './pages/OpportunitiesDashboard';

// Components
import OnboardingModal from './components/OnboardingModal';

// Services
import { authAPI, onboardingAPI, getToken, removeToken } from './services/api';
import { setUser as setSentryUser, clearUser as clearSentryUser, SentryErrorBoundary } from './services/sentry';

// Fallback UI shown when an uncaught exception bubbles up from any route.
// Prevents the whole SPA from unmounting to a blank screen.
const AppErrorFallback = ({ resetError }) => (
  <div className="min-h-screen flex items-center justify-center bg-[#0a0f1a] px-6">
    <div className="max-w-md text-center">
      <h1 className="text-2xl font-semibold text-white mb-3">Une erreur est survenue</h1>
      <p className="text-gray-400 mb-6">
        Un problème inattendu a interrompu l&apos;affichage de cette page. L&apos;incident a été enregistré. Vous pouvez recharger la page ou revenir à l&apos;accueil.
      </p>
      <div className="flex gap-3 justify-center">
        <button
          data-testid="error-boundary-retry"
          onClick={resetError}
          className="bg-emerald-500 hover:bg-emerald-600 text-white px-6 py-2 rounded-lg transition-colors"
        >
          Réessayer
        </button>
        <button
          data-testid="error-boundary-home"
          onClick={() => { window.location.href = '/'; }}
          className="bg-[#1a1a2e] hover:bg-[#2a2a3e] border border-[#2a2a3e] text-white px-6 py-2 rounded-lg transition-colors"
        >
          Retour à l&apos;accueil
        </button>
      </div>
    </div>
  </div>
);

// Protected Route Component with subscription check
const ProtectedRoute = ({ children, user, setUser }) => {
  const [authState, setAuthState] = useState(user ? 'authenticated' : 'checking');
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (location.state?.user) {
      setUser(location.state.user);
      setAuthState('authenticated');
      return;
    }

    const checkAuth = async () => {
      const token = getToken();
      if (!token) {
        setAuthState('unauthenticated');
        navigate('/login', { state: { from: location.pathname } });
        return;
      }
      try {
        // Always fetch fresh user data from API (no stale cache)
        const userData = await authAPI.getMe();
        if (userData) {
          setUser(userData);
          // Check subscription access
          if (userData.has_access === false) {
            // Explicitly no access
            setAuthState('no_access');
            navigate('/pricing', { state: { message: 'Votre abonnement n\'est pas actif. Veuillez souscrire pour accéder au screener.' } });
            return;
          } else if (userData.has_access === undefined || userData.has_access === null) {
            // Access status unknown - subscription may be activating
            setAuthState('pending_activation');
            return;
          }
          // has_access === true
          setAuthState('authenticated');
        }
      } catch (error) {
        // Token invalid - clear and redirect
        removeToken();
        setAuthState('unauthenticated');
        navigate('/login', { state: { from: location.pathname } });
      }
    };

    // Always re-check auth on navigation to get fresh data
    checkAuth();
  }, [location.pathname, setUser, navigate]);

  if (authState === 'checking') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0f1a]">
        <div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (authState === 'pending_activation') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0f1a]">
        <div className="text-center max-w-md p-8">
          <div className="w-16 h-16 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-6"></div>
          <h2 className="text-xl font-semibold text-white mb-3">Activation en cours</h2>
          <p className="text-gray-400 mb-6">
            Votre abonnement est en cours d'activation. Cela peut prendre quelques minutes après le paiement.
          </p>
          <button 
            onClick={() => window.location.reload()} 
            className="bg-emerald-500 hover:bg-emerald-600 text-white px-6 py-2 rounded-lg transition-colors"
          >
            Actualiser la page
          </button>
          <p className="text-gray-500 text-sm mt-4">
            Si le problème persiste après 5 minutes, contactez le support.
          </p>
        </div>
      </div>
    );
  }

  if (authState === 'unauthenticated' || authState === 'no_access') {
    return null; // Navigate will handle redirect
  }

  return children;
};

// App Router Component
const AppRouter = ({ user, setUser, handleLogout }) => {
  return (
    <div className="min-h-screen bg-[#0a0f1a] flex flex-col">
      <Header user={user} onLogout={handleLogout} />
      <main className="flex-1">
        <Routes>
          <Route path="/" element={user ? <OpportunitiesDashboard /> : <LandingPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/cgv" element={<CGVPage />} />
          <Route path="/payment/success" element={<PaymentSuccessPage />} />
          <Route path="/login" element={<LoginPage user={user} setUser={setUser} />} />
          <Route path="/auth/callback" element={<AuthCallbackPage setUser={setUser} />} />
          <Route path="/markets" element={<MarketsPage user={user} />} />
          <Route path="/markets/:marketCode" element={<MarketsPage user={user} />} />
          <Route path="/learn/valuation-guide" element={<ValuationGuidePage />} />
          <Route path="/preview" element={<Navigate to="/screener" replace />} />
          <Route path="/screener" element={<ScreenerPage user={user} />} />
          <Route path="/reports" element={
            <ProtectedRoute user={user} setUser={setUser}>
              <ReportsPage />
            </ProtectedRoute>
          } />
          <Route path="/reports/:market/:symbol" element={
            <ProtectedRoute user={user} setUser={setUser}>
              <ReportDetailPage />
            </ProtectedRoute>
          } />
          <Route path="/stock/:symbol" element={
            <ProtectedRoute user={user} setUser={setUser}>
              <StockDetailPage />
            </ProtectedRoute>
          } />
          <Route path="/watchlist"
            element={
              <ProtectedRoute user={user} setUser={setUser}>
                <WatchlistPage user={user} />
              </ProtectedRoute>
            }
          />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <Footer />
    </div>
  );
};

function App() {
  const [user, setUser] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [preferredMarket, setPreferredMarket] = useState(null);

  // Check for existing JWT on mount
  useEffect(() => {
    const checkSession = async () => {
      const token = getToken();
      if (!token) return;

      try {
        const userData = await authAPI.getMe();
        if (userData) {
          setUser(userData);
          setSentryUser(userData.user_id, userData.email);

          try {
            const onboardingStatus = await onboardingAPI.getStatus();
            if (!onboardingStatus.has_seen_onboarding) {
              setShowOnboarding(true);
            }
            if (onboardingStatus.preferred_market) {
              setPreferredMarket(onboardingStatus.preferred_market);
            }
          } catch (e) {
            // Onboarding check failed, continue
          }
        }
      } catch (error) {
        // Token invalid or expired — clear it
        removeToken();
      }
    };
    checkSession();
  }, []);

  const handleOnboardingComplete = async (selectedMarket) => {
    try {
      await onboardingAPI.complete(selectedMarket);
      if (selectedMarket) setPreferredMarket(selectedMarket);
    } catch (error) {
      console.error('Failed to save onboarding:', error);
    }
    setShowOnboarding(false);
  };

  const handleLogout = () => {
    authAPI.logout();
    setUser(null);
    clearSentryUser();
  };

  return (
    <BrowserRouter>
      <SentryErrorBoundary fallback={AppErrorFallback}>
        <AppRouter
          user={user}
          setUser={setUser}
          handleLogout={handleLogout}
          preferredMarket={preferredMarket}
        />

        <OnboardingModal
          isOpen={showOnboarding}
          onClose={() => setShowOnboarding(false)}
          onComplete={handleOnboardingComplete}
        />
      </SentryErrorBoundary>
    </BrowserRouter>
  );
}

export default App;
